import React from 'react';
import AddIcon from '@mui/icons-material/Add';
import { Button, CardMedia, Typography, FormHelperText, Grid } from '@mui/material';
import logoImage from '../../assets/Group.svg';
import { gray80, orange50, red50, secondaryButtonBackgroundColor } from '../../../constants/colors';
import { formLabelWrapper } from '../../../constants/location';
import type { FieldError, FieldErrorsImpl, Merge } from 'react-hook-form';

interface Props {
  type: string;
  preview: string | null;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  requiredText?: string;
  isRequired?: boolean;
  error?: boolean;
  helperText?:
    | string
    | FieldError
    | Merge<FieldError, FieldErrorsImpl<Record<string, unknown>>>
    | undefined;
  otherError?: string | null | null;
  disabled?: boolean;
  formMb?: string | undefined;
}

const CustomImageUploader = React.forwardRef(
  (props: Props, _ref): React.ReactElement | JSX.Element => {
    const {
      type,
      preview,
      onChange,
      requiredText,
      isRequired,
      error,
      otherError,
      helperText,
      formMb,
      disabled,
    } = props;

    let image;

    const styles = {
      bgColor: {
        bgcolor: '#DBDBE0',
      },
      formLabelWrapper,
      helper: {
        '@media screen and (max-width:480px)': {
          marginLeft: 0,
        },
      },
      imageIcon: {
        bgcolor: gray80,
        height: '40px',
        left: '50%',
        position: 'absolute',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: '40px',
      },
      imagePersonIcon: {
        '@media screen and (max-width:480px)': {
          left: '10%',
        },
        height: '40px',
        left: '50%',
        position: 'absolute',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: '40px',
      },
      label: {
        display: 'block',
        fontSize: '12px',
        mb: '8px',
      },
      required: {
        color: red50,
        fontSize: '12px',
        marginLeft: '10px',
      },
      uploadButton: {
        backgroundColor: '#FFF',
        boxShadow: 'none',
        width: 'auto',
        padding: '10px 16px',
        border: `1px solid ${orange50}`,
        fontSize: '16px',
        fontWeight: '700',
        color: secondaryButtonBackgroundColor,
      },
      formMb: { marginBottom: formMb ?? '8px' },
    };

    if (type === 'icon') {
      image = {
        borderRadius: '50%',
        height: '80px',
        width: '80px',
        '@media screen and (max-width:480px)': {
          height: '64px',
          width: '64px',
        },
      };
    }

    const textfieldHelper = (): string | undefined => {
      if (helperText === undefined) return;
      return String(helperText);
    };

    return (
      <>
        <Typography component="div" sx={styles.formMb}>
          <Typography
            align="left"
            sx={[
              styles.formLabelWrapper,
              {
                display: 'flex',
                flexDirection: 'row',
              },
            ]}
            component="div"
          >
            <Typography color={gray80} sx={styles.label} component="span">
              アイコン
            </Typography>
            {isRequired ? (
              <Typography component="span" sx={styles.required}>
                {requiredText}
              </Typography>
            ) : (
              ''
            )}
          </Typography>
          <Grid
            container
            sx={{
              alignItems: 'center',
              columnGap: '16px',
              flexDirection: 'row',
              rowGap: '16px',
            }}
          >
            {preview ? (
              <CardMedia image={preview} sx={image} />
            ) : (
              <Typography
                component="div"
                sx={{
                  '@media screen and (max-width:480px)': {
                    height: '80px',
                    width: '80px',
                  },
                  position: 'relative',
                }}
              >
                <img src={logoImage} />
              </Typography>
            )}
            <Typography
              component="div"
              sx={{
                columnGap: '16px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <Button
                component="label"
                variant="contained"
                sx={styles.uploadButton}
                disabled={disabled}
              >
                <input
                  accept=".jpg, .jpeg, .png, .webp, .gif, .svg"
                  type="file"
                  hidden
                  onChange={onChange}
                />
                <AddIcon />
                ファイルを選択する
              </Button>
            </Typography>
          </Grid>
          <FormHelperText sx={styles.helper} error={error}>
            {textfieldHelper()}
          </FormHelperText>
          <FormHelperText sx={styles.helper} error={!!otherError}>
            {otherError}
          </FormHelperText>
        </Typography>
      </>
    );
  },
);

CustomImageUploader.displayName = 'CustomImageUploader';
export default CustomImageUploader;
